import React from "react";
import { Table, Button, InputNumber, Alert } from "antd";
import { ChannelMode } from "../../../flatbuffers/generated/archive/channel-mode";
import { ChannelType } from "../../../flatbuffers/generated/archive/channel-type";

export interface EndpointRow {
  key: string;
  address: string;
  groupId: number;
  priority: number;
  transportType: ChannelType;
  baudRate: number;
  adapterId: string;
}

interface EndpointTableProps {
  mode: ChannelMode;
  rows: EndpointRow[];
  onChange: (rows: EndpointRow[]) => void;
  channelTransportType: ChannelType;
}

export function validateEndpoints(mode: ChannelMode, rows: EndpointRow[]): string | null {
  if (rows.length === 0) return "At least one endpoint is required.";

  // ISC-125: reject mixed transport types
  const types = new Set(rows.map((r) => r.transportType));
  if (types.size > 1) return "All endpoints must use the same transport type.";

  if (mode === ChannelMode.SINGLE) {
    if (rows.length > 1) return "SINGLE mode allows exactly one endpoint.";
    return null;
  }
  if (mode === ChannelMode.FAILOVER) {
    const priorityKeys = rows.map((r) => `${r.groupId}:${r.priority}`);
    if (new Set(priorityKeys).size !== priorityKeys.length)
      return "FAILOVER mode requires unique (group_id, priority) pairs.";
    return null;
  }
  if (mode === ChannelMode.MERGE) {
    if (rows.some((r) => r.priority !== 0))
      return "MERGE mode requires priority = 0 for all endpoints.";
    return null;
  }
  if (mode === ChannelMode.MIXED) {
    const groups = Array.from(new Set(rows.map((r) => r.groupId)));
    const emptyGroup = groups.find((g) => rows.filter((r) => r.groupId === g).length === 0);
    if (emptyGroup !== undefined) return `Group ${emptyGroup} has no endpoints.`;
    return null;
  }
  return null;
}

const CHANNEL_TYPE_LABEL: Record<number, string> = {
  [ChannelType.SERIAL]: "SERIAL",
  [ChannelType.TCP]: "TCP",
  [ChannelType.UDP]: "UDP",
  [ChannelType.ADAPTER]: "ADAPTER",
  [ChannelType.TCP_BOT]: "TCP_BOT",
  [ChannelType.HTTP]: "HTTP",
};

export default function EndpointTable({
  mode,
  rows,
  onChange,
  channelTransportType,
}: EndpointTableProps) {
  const validationError = validateEndpoints(mode, rows);

  const updateRow = (index: number, patch: Partial<EndpointRow>) => {
    const next = rows.map((r, i) => (i === index ? { ...r, ...patch } : r));
    onChange(next);
  };

  const handleAddEndpoint = () => {
    const currentMaxGroupId = rows.length > 0 ? Math.max(0, ...rows.map((r) => r.groupId)) : 0;
    const rowsInGroup = rows.filter((r) => r.groupId === currentMaxGroupId);
    const newPriority =
      rowsInGroup.length > 0 ? Math.max(0, ...rowsInGroup.map((r) => r.priority)) + 1 : 0;
    const newRow: EndpointRow = {
      key: crypto.randomUUID(),
      address: "",
      groupId: currentMaxGroupId,
      priority: newPriority,
      transportType: channelTransportType,
      baudRate: 9600,
      adapterId: "",
    };
    onChange([...rows, newRow]);
  };

  const handleRemoveRow = (index: number) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const showGroupColumn = mode !== ChannelMode.FAILOVER;
  const showBaudRateColumn = channelTransportType === ChannelType.SERIAL;

  const columns = [
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (_: string, record: EndpointRow, index: number) => (
        <input
          data-testid={`endpoint-address-${index}`}
          className="ant-input ant-input-middle"
          style={{
            width: "100%",
            border: record.address.trim() === "" ? "1px solid #ff4d4f" : undefined,
          }}
          value={record.address}
          placeholder="192.168.1.100:4840"
          onChange={(e) => updateRow(index, { address: e.target.value })}
        />
      ),
    },
    ...(showGroupColumn
      ? [
          {
            title: "Group",
            dataIndex: "groupId",
            key: "groupId",
            render: (_: number, record: EndpointRow, index: number) => (
              <InputNumber
                min={0}
                value={record.groupId}
                onChange={(val) => updateRow(index, { groupId: val ?? 0 })}
                style={{ width: 70 }}
              />
            ),
          },
        ]
      : []),
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (_: number, record: EndpointRow, index: number) => (
        <InputNumber
          data-testid={`endpoint-priority-${index}`}
          min={0}
          value={record.priority}
          onChange={(val) => updateRow(index, { priority: val ?? 0 })}
          style={{ width: 70 }}
        />
      ),
    },
    {
      title: "Transport",
      dataIndex: "transportType",
      key: "transportType",
      render: (val: ChannelType) => CHANNEL_TYPE_LABEL[val] ?? String(val),
    },
    ...(showBaudRateColumn
      ? [
          {
            title: "Baud Rate",
            dataIndex: "baudRate",
            key: "baudRate",
            render: (_: number, record: EndpointRow, index: number) => (
              <InputNumber
                min={300}
                max={115200}
                value={record.baudRate}
                onChange={(val) => updateRow(index, { baudRate: val ?? 9600 })}
                style={{ width: 90 }}
              />
            ),
          },
        ]
      : []),
    {
      title: "Adapter Override",
      dataIndex: "adapterId",
      key: "adapterId",
      render: (_: string, record: EndpointRow, index: number) => (
        <input
          className="ant-input ant-input-middle"
          style={{ width: "100%" }}
          value={record.adapterId}
          placeholder="(inherit from bank)"
          onChange={(e) => updateRow(index, { adapterId: e.target.value })}
        />
      ),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (_: unknown, _record: EndpointRow, index: number) => (
        <Button
          data-testid={`endpoint-remove-${index}`}
          type="link"
          danger
          size="small"
          disabled={rows.length <= 1}
          onClick={() => handleRemoveRow(index)}
        >
          Remove
        </Button>
      ),
    },
  ];

  return (
    <div data-testid="endpoint-table">
      {validationError != null && (
        <Alert
          data-testid="endpoint-validation-error"
          type="error"
          showIcon
          message={validationError}
          style={{ marginBottom: 8 }}
        />
      )}
      <Table
        dataSource={rows}
        columns={columns}
        pagination={false}
        size="small"
        locale={{ emptyText: "No endpoints" }}
      />
      <Button
        data-testid="add-endpoint-btn"
        type="dashed"
        onClick={handleAddEndpoint}
        style={{ marginTop: 8 }}
      >
        Add endpoint
      </Button>
    </div>
  );
}
